import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, Loader2, KeyRound, Lock } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuthStore } from "@/stores";
import { Button, Input, Label } from "@/components/ui";
import { cn, SHOP_NAME } from "@/lib/utils";

const passwordLoginSchema = z.object({
  username: z.string().min(1, "Username is required"),
  password: z.string().min(1, "Password is required"),
});

const pinLoginSchema = z.object({
  username: z.string().min(1, "Username is required"),
  pin: z.string().regex(/^\d{4,6}$/, "PIN must be 4-6 digits"),
});

type PasswordLoginData = z.infer<typeof passwordLoginSchema>;
type PinLoginData = z.infer<typeof pinLoginSchema>;

interface LocationState {
  from?: { pathname?: string };
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithPin, isLoading, error, clearError } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [loginMode, setLoginMode] = useState<"password" | "pin">("password");

  const shopName = SHOP_NAME;
  const from = (location.state as LocationState)?.from?.pathname ?? "/";

  const passwordForm = useForm<PasswordLoginData>({
    resolver: zodResolver(passwordLoginSchema),
    defaultValues: { username: "", password: "" },
  });

  const pinForm = useForm<PinLoginData>({
    resolver: zodResolver(pinLoginSchema),
    defaultValues: { username: "", pin: "" },
  });

  const handleModeChange = (mode: "password" | "pin") => {
    clearError();
    setLoginMode(mode);
  };

  const onPasswordSubmit = async (data: PasswordLoginData) => {
    clearError();
    try {
      await login(data.username, data.password);
      navigate(from, { replace: true });
    } catch {
    }
  };

  const onPinSubmit = async (data: PinLoginData) => {
    clearError();
    try {
      await loginWithPin(data.username, data.pin);
      navigate(from, { replace: true });
    } catch {
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-coffee-700 via-coffee-800 to-coffee-900 p-4">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_20%,rgba(255,255,255,0.06),transparent_45%)]" />
      <div className="relative w-full max-w-md rounded-2xl bg-white p-10 shadow-2xl">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-primary">{shopName}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Coffee Shop POS System
          </p>
        </div>
        <div className="mt-6 flex rounded-lg border bg-muted p-1">
          <button
            type="button"
            onClick={() => handleModeChange("password")}
            className={cn(
              "flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
              loginMode === "password"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Lock className="h-4 w-4" />
            Password
          </button>
          <button
            type="button"
            onClick={() => handleModeChange("pin")}
            className={cn(
              "flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
              loginMode === "pin"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <KeyRound className="h-4 w-4" />
            PIN
          </button>
        </div>
        {error && (
          <div
            role="alert"
            className="mt-4 rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </div>
        )}

        {loginMode === "password" ? (
          <form
            onSubmit={passwordForm.handleSubmit(onPasswordSubmit)}
            className="mt-4 space-y-4"
            noValidate
          >
            <div className="space-y-1.5">
              <Label htmlFor="username" className="sr-only">
                Username
              </Label>
              <Input
                id="username"
                placeholder="Username"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                disabled={isLoading}
                aria-invalid={Boolean(passwordForm.formState.errors.username)}
                {...passwordForm.register("username")}
              />
              {passwordForm.formState.errors.username && (
                <p className="text-xs text-destructive">
                  {passwordForm.formState.errors.username.message}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="sr-only">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  autoComplete="current-password"
                  disabled={isLoading}
                  aria-invalid={Boolean(passwordForm.formState.errors.password)}
                  {...passwordForm.register("password")}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-2 flex items-center px-2 text-muted-foreground transition-colors hover:text-foreground"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {passwordForm.formState.errors.password && (
                <p className="text-xs text-destructive">
                  {passwordForm.formState.errors.password.message}
                </p>
              )}
            </div>
            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={isLoading}
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sign In
            </Button>
          </form>
        ) : (
          <form
            onSubmit={pinForm.handleSubmit(onPinSubmit)}
            className="mt-4 space-y-4"
            noValidate
          >
            <div className="space-y-1.5">
              <Label htmlFor="pin-username" className="sr-only">
                Username
              </Label>
              <Input
                id="pin-username"
                placeholder="Username"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                disabled={isLoading}
                aria-invalid={Boolean(pinForm.formState.errors.username)}
                {...pinForm.register("username")}
              />
              {pinForm.formState.errors.username && (
                <p className="text-xs text-destructive">
                  {pinForm.formState.errors.username.message}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pin" className="sr-only">
                PIN
              </Label>
              <Input
                id="pin"
                type="password"
                inputMode="numeric"
                maxLength={6}
                placeholder="Enter 4-6 digit PIN"
                autoComplete="off"
                disabled={isLoading}
                aria-invalid={Boolean(pinForm.formState.errors.pin)}
                {...pinForm.register("pin")}
              />
              {pinForm.formState.errors.pin && (
                <p className="text-xs text-destructive">
                  {pinForm.formState.errors.pin.message}
                </p>
              )}
            </div>
            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={isLoading}
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sign In with PIN
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
